import Chart, { useChart } from 'src/components/chart';
import { Box, useTheme } from '@mui/material';
import { formatCurrency } from 'src/utils/format-currency';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatNumber } from 'src/utils/format-number';

type SeriesItem = {
    name: string;
    data: number[];
};

type Props = {
    series: SeriesItem[];
    categories: any[];
    metrics: any;
    breakdownDimension: string;
};

// Capitalize first letter, lowercase rest
const capitalizeFirst = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

// Palette country (dans l'ordre donné)
const countryPalette = [
    '#003768',
    '#006C9C',
    '#66a4d1',
    '#a5d8fe',
    '#CAFDF5',
    '#D3FCD2',
    '#77ED8B',
    '#22C55E',
    '#118D57',
    '#065E49',
];

// Détermine si on doit arrondir pour un nom de série ou métrique
const shouldRoundSeries = (seriesName: string, breakdownDimension: string, metrics: any) => {
    const n = seriesName.trim().toLowerCase();
    if (breakdownDimension === 'channel') return true; // Paid / Organic / Revenue on arrondit
    if (String(metrics).trim().toLowerCase() === 'conversions') return true;
    return false;
};

export default function ChartAreaSpline({ series, categories, metrics, breakdownDimension }: Props) {
    const theme = useTheme();
    const workspace: any = useCurrentWorkspace();
    const lang: any = localStorage.getItem('i18nextLng');

    // Couleurs par dimension
    const platformColorMap: Record<string, string> = {
        google: '#66a4d1',
        bing: '#006C9C',
    };

    const devicesColorMap = ['#006C9C', theme.palette.paid?.dark || '#1E88E5', theme.palette.organic?.dark || '#22C55E'];

    const channelColorsMap: Record<string, string> = {
        Paid: "rgba(0, 108, 156, 0.5)",
        Organic: "rgba(17, 141, 87, 0.5)",
        Revenue: theme.palette.revenue?.dark || '#006C9C',
    };

    // Filtrer/sélectionner séries + builder palette stable
    let filteredSeries: SeriesItem[] = [];
    let colorPalette: string[] = [];

    if (breakdownDimension === 'channel') {
        filteredSeries = series.filter((s) => s.name === 'Paid' || s.name === 'Organic').slice(0, 2);
        colorPalette = filteredSeries.map((s) => channelColorsMap[s.name] || '#B7DCFB');
    } else if (breakdownDimension === 'platform') {
        filteredSeries = series.slice(0, 2).map(s => ({ ...s, name: capitalizeFirst(s.name) }));
        colorPalette = filteredSeries.map(s => {
            const key = s.name.toLowerCase();
            return platformColorMap[key] || '#1E88E5';
        });
    } else if (breakdownDimension === 'device') {
        filteredSeries = series.slice(0, 3).map(s => ({ ...s, name: capitalizeFirst(s.name) }));
        colorPalette = devicesColorMap.slice(0, filteredSeries.length);
    } else if (breakdownDimension === 'country') {
        // garder l'ordre d'arrivée pour attribuer une couleur stable
        const countryColorMap: Record<string, string> = {};
        let nextIdx = 0;
        filteredSeries = series.slice(0, 10).map(s => ({ name: s.name, data: s.data })); // on conserve tout
        filteredSeries.forEach(s => {
            const key = s.name.trim().toLowerCase();
            if (!(key in countryColorMap)) {
                countryColorMap[key] = countryPalette[nextIdx % countryPalette.length];
                nextIdx += 1;
            }
        });
        colorPalette = filteredSeries.map(s => countryColorMap[s.name.trim().toLowerCase()] || '#1E88E5');
    } else {
        filteredSeries = series.slice(0, 10);
        // fallback palette
        const fallback = ['#1E88E5', '#64B5F6', '#B7DCFB', '#006C9C'];
        colorPalette = filteredSeries.map((_, i) => fallback[i % fallback.length]);
    }

    // Appliquer l'arrondi si nécessaire sur les données
    const processedSeries: SeriesItem[] = filteredSeries.map((s) => {
        const round = shouldRoundSeries(s.name, breakdownDimension, metrics);
        return {
            name: s.name,
            data: round ? s.data.map((d) => Math.round(d)) : s.data.slice(),
        };
    });

    // Prépare catégories avec capitalisation si besoin
    const displayCategories = (breakdownDimension === 'platform' || breakdownDimension === 'device')
        ? categories.map((c: any) => (typeof c === 'string' ? capitalizeFirst(c) : c))
        : categories;

    const chartOptions = useChart({
        chart: {
            type: 'area',
            height: 370,
            stacked: true,
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        legend: {
            itemMargin: {
                vertical: 8,
            },
            markers: {
                customHTML: function () {
                    return '<div style="width: 16px; height: 16px; border-radius: 50%; background-color: currentcolor;"></div>';
                },
            },
            position: 'top',
            offsetY: 0,
        },
        colors: colorPalette,
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                inverseColors: false,
                opacityFrom: 0.4,
                opacityTo: 0.1,
                stops: [0, 90, 100],
            },
        },
        xaxis: {
            type: 'category',
            categories: displayCategories,
            labels: {
                formatter: (val: any) => {
                    if (breakdownDimension === 'platform' || breakdownDimension === 'device') {
                        return typeof val === 'string' ? capitalizeFirst(val) : val;
                    }
                    return val;
                },
            },
        },
        tooltip: {
            x: { format: 'dd MMM yyyy' },
            shared: true,
            y: {
                formatter: (value: number, { seriesIndex }: any) => {
                    const seriesName = processedSeries[seriesIndex]?.name || '';
                    if (String(metrics).toLowerCase() === 'revenue') {
                        return formatCurrency(value, workspace.currency, lang, true, 1);
                    }
                    if (String(metrics).toLowerCase() === 'conversions' || breakdownDimension === 'channel') {
                        return `${formatNumber(value)}`;
                    }
                    return value?.toString();
                },
            },
        },
        yaxis: {
            labels: {
                formatter: (value: number) => {
                    if (String(metrics).toLowerCase() === 'revenue') {
                        return formatCurrency(value, workspace.currency, lang, true, 1);
                    }
                    if (String(metrics).toLowerCase() === 'conversions' || breakdownDimension === 'channel') {
                        return `${Math.round(value)}`;
                    }
                    return value?.toString();
                },
            },
        },
    });

    return (
        <Box>
            <Chart options={chartOptions} series={processedSeries} type="area" height={370} />
        </Box>
    );
}
